/**
 * NCR (Non-Conformance Report) Page
 *
 * Create and manage Non-Conformance Reports for audit findings.
 * Allows auditors to document non-conformities and track corrective actions.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';

export const NCRPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('app') || '';

  const [formData, setFormData] = useState({
    ncrNumber: `NCR-${Date.now()}`,
    applicationId: applicationId,
    companyName: 'Kantin Warisan Sdn Bhd',
    severity: 'MINOR',
    clause: '',
    standard: 'MHMS 2020',
    finding: '',
    evidence: '',
    correctiveAction: '',
    responsiblePerson: '',
    targetDate: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`NCR ${formData.ncrNumber} created successfully!`);
    // Navigate back to application review
    window.location.href = `/auditor/applications/${applicationId}`;
  };

  return (
    <PageLayout
      pageTitle={t('auditor:ncr.create')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
        { label: t('auditor:ncr.create') },
      ]}
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* NCR Header Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">NCR Information</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.ncrNumber')}
                  </label>
                  <Input
                    name="ncrNumber"
                    value={formData.ncrNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('ncrNumber', e.target.value)}
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('companyName', e.target.value)}
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.severity')} *
                  </label>
                  <select
                    value={formData.severity}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('severity', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="MINOR">{t('auditor:ncr.minor')}</option>
                    <option value="MAJOR">{t('auditor:ncr.major')}</option>
                    <option value="OBSERVATION">{t('auditor:ncr.observation')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.targetDate')} *
                  </label>
                  <Input
                    type="date"
                    name="targetDate"
                    value={formData.targetDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('targetDate', e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Finding Details */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Finding Details</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auditor:ncr.standard')}
                    </label>
                    <Input
                      name="standard"
                      value={formData.standard}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('standard', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auditor:ncr.clause')} *
                    </label>
                    <Input
                      name="clause"
                      value={formData.clause}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('clause', e.target.value)}
                      placeholder="e.g., 4.2.3"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.finding')} *
                  </label>
                  <textarea
                    value={formData.finding}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('finding', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the non-conformity found..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.evidence')} *
                  </label>
                  <textarea
                    value={formData.evidence}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('evidence', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Provide objective evidence of the finding..."
                    required
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Corrective Action Required */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">{t('auditor:ncr.correctiveActionRequired')}</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Action *
                  </label>
                  <textarea
                    value={formData.correctiveAction}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('correctiveAction', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the corrective action required to address this finding..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auditor:ncr.responsiblePerson')} *
                  </label>
                  <Input
                    name="responsiblePerson"
                    value={formData.responsiblePerson}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('responsiblePerson', e.target.value)}
                    placeholder="Name of person responsible for implementation"
                    required
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardBody>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button type="submit" fullWidth icon={<FileText className="w-4 h-4" />}>
                  Create NCR
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </form>
    </PageLayout>
  );
};
